/**
 * Mercenary Stats Calculation Module
 *
 * Follows the same pattern as getPlayerStats() in GameContext
 * to calculate combat stats from primary stats.
 */

import { MERCENARY_CLASSES } from '../../data/outposts/mercenaries';

/**
 * Calculate combat stats for a mercenary from their primary stats
 * Uses the same formulas as player character stats
 *
 * @param {Object} mercenary - Mercenary data object
 * @returns {Object} Combat stats { health, maxHealth, attack, defense, critChance, critDamage, speed, accuracy, evasion }
 */
export function calculateMercenaryStats(mercenary) {
  const { STR, DEX, INT, LUK } = mercenary.primaryStats;
  const quality = mercenary.quality || 1.0;
  const level = mercenary.level || 1;

  // Base stats (same formulas as player, slightly reduced base values)
  const baseHealth = 50 + (STR * 5) + (INT * 2);
  const baseAttack = 5 + (STR * 1.5) + (DEX * 0.5);
  const baseDefense = 2 + (STR * 0.5) + (INT * 0.8);
  const baseCritChance = 0.05 + (DEX * 0.003) + (LUK * 0.004);
  const baseCritDamage = 1.5 + (LUK * 0.01);
  const baseAccuracy = 0.80 + (DEX * 0.005) + (INT * 0.003);
  const baseEvasion = 0.05 + (DEX * 0.003) + (LUK * 0.002);
  const baseSpeed = 1.0 + (DEX * 0.01);

  // Level bonuses (smaller than player)
  const levelHealth = level * 5;
  const levelAttack = level * 1;
  const levelDefense = level * 0.5;

  // Apply quality multiplier
  const health = Math.floor((baseHealth + levelHealth) * quality);
  const attack = Math.floor((baseAttack + levelAttack) * quality);
  const defense = Math.floor((baseDefense + levelDefense) * quality);

  return {
    health,
    maxHealth: health,
    attack,
    defense,
    critChance: Math.min(0.80, baseCritChance * quality),
    critDamage: baseCritDamage * quality,
    accuracy: Math.min(0.99, baseAccuracy),
    evasion: Math.min(0.50, baseEvasion),
    speed: baseSpeed,
  };
}

/**
 * Calculate total squad power for mission success rate calculation
 * Power is a weighted combination of stats
 *
 * @param {Array} mercenaries - Array of mercenary objects
 * @returns {number} Total squad power
 */
export function calculateSquadPower(mercenaries) {
  if (!mercenaries || mercenaries.length === 0) {
    return 0;
  }

  return mercenaries.reduce((total, merc) => {
    // Skip dead mercenaries
    if (merc.status === 'dead') {
      return total;
    }

    const stats = calculateMercenaryStats(merc);

    // Weighted power formula
    // Attack is most important, then defense, then health
    const power = (
      stats.health * 0.3 +
      stats.attack * 2.0 +
      stats.defense * 1.5 +
      stats.critChance * 100 +
      stats.critDamage * 20
    );

    return total + power;
  }, 0);
}

/**
 * Calculate average level of a squad
 * @param {Array} mercenaries - Array of mercenary objects
 * @returns {number} Average level
 */
export function getSquadAverageLevel(mercenaries) {
  if (!mercenaries || mercenaries.length === 0) {
    return 0;
  }

  const totalLevel = mercenaries.reduce((sum, merc) => sum + (merc.level || 1), 0);
  return Math.floor(totalLevel / mercenaries.length);
}

/**
 * Get summary stats for a squad (for UI display)
 * @param {Array} mercenaries - Array of mercenary objects
 * @returns {Object} Summary stats { totalHealth, totalAttack, totalDefense, avgLevel, power }
 */
export function getSquadSummary(mercenaries) {
  if (!mercenaries || mercenaries.length === 0) {
    return {
      totalHealth: 0,
      totalAttack: 0,
      totalDefense: 0,
      avgLevel: 0,
      power: 0,
      memberCount: 0,
    };
  }

  let totalHealth = 0;
  let totalAttack = 0;
  let totalDefense = 0;

  mercenaries.forEach(merc => {
    if (merc.status !== 'dead') {
      const stats = calculateMercenaryStats(merc);
      totalHealth += stats.health;
      totalAttack += stats.attack;
      totalDefense += stats.defense;
    }
  });

  return {
    totalHealth,
    totalAttack,
    totalDefense,
    avgLevel: getSquadAverageLevel(mercenaries),
    power: calculateSquadPower(mercenaries),
    memberCount: mercenaries.filter(m => m.status !== 'dead').length,
  };
}

/**
 * Check if mercenary can level up
 * @param {Object} mercenary - Mercenary data
 * @returns {boolean} True if can level up
 */
export function canLevelUp(mercenary) {
  return mercenary.experience >= mercenary.experienceToNext;
}

/**
 * Apply level up to mercenary (returns new mercenary object)
 * @param {Object} mercenary - Mercenary data
 * @returns {Object} Updated mercenary with new level and stats
 */
export function applyLevelUp(mercenary) {
  if (!canLevelUp(mercenary)) {
    return mercenary;
  }

  const mercClass = MERCENARY_CLASSES[mercenary.class];
  if (!mercClass) {
    return mercenary;
  }

  const newLevel = mercenary.level + 1;
  const remainingXp = mercenary.experience - mercenary.experienceToNext;
  const newXpToLevel = Math.floor(100 * Math.pow(1.15, newLevel - 1));

  // Distribute 3 stat points weighted by class
  const weights = mercClass.statWeights;
  const totalWeight = weights.STR + weights.DEX + weights.INT + weights.LUK;
  const newStats = { ...mercenary.primaryStats };

  for (let i = 0; i < 3; i++) {
    const roll = Math.random() * totalWeight;
    let cumulative = 0;

    for (const stat of ['STR', 'DEX', 'INT', 'LUK']) {
      cumulative += weights[stat];
      if (roll <= cumulative) {
        newStats[stat]++;
        break;
      }
    }
  }

  return {
    ...mercenary,
    level: newLevel,
    experience: remainingXp,
    experienceToNext: newXpToLevel,
    primaryStats: newStats,
  };
}
